import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  alpha,
  useTheme,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useIdpService } from "@art/shared-ui";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { useLocation, useNavigate } from "react-router-dom";

interface ExpiryStatus {
  phase: "active" | "expiring_soon" | "grace_period" | "expired" | "no_subscription";
  application_code?: string;
  days_until_expiry?: number;
  grace_days_left?: number;
  show_banner: boolean;
  show_renewal_modal: boolean;
  message?: string;
}


const ROUTE_CATEGORY_MAP: { prefix: string; categoryCode: string }[] = [
  { prefix: "/agent-builder", categoryCode: "AGENT_LAB" },
  { prefix: "/datalake", categoryCode: "DATALAKE" },
  { prefix: "/live", categoryCode: "LIVE_CONNECT" },
];


const SEVERITY: Record<ExpiryStatus["phase"], number> = {
  expired: 3,
  grace_period: 2,
  expiring_soon: 1,
  active: 0,
  no_subscription: 0,
};

const SubscriptionBanner = () => {
  const theme = useTheme();
  const idpService = useIdpService();
  const navigate = useNavigate();
  const location = useLocation();
  const tenantName = useSelector((state: RootState) => state.auth.tenantName);
  const [expiryStatuses, setExpiryStatuses] = useState<ExpiryStatus[]>([]);
  const [dismissedKey, setDismissedKey] = useState<string | null>(null);

  useEffect(() => {
    if (!tenantName) return;

    idpService
      .get(`api/tenants/${tenantName}/subscription/expiry-status`, {
        showErrorSnack: false,
      })
      .then((response: any) => {
        const data = response?.data?.data || response?.data;
        if (!data) return;
        setExpiryStatuses(Array.isArray(data) ? data : [data]);
      })
      .catch((error: any) => {
        console.error("Failed to fetch subscription expiry status:", error);
      });
  }, [tenantName]);

  const expiryStatus = useMemo(() => {
    if (expiryStatuses.length === 0) return null;

    const matchedPrefix = ROUTE_CATEGORY_MAP.find((r) =>
      location.pathname.startsWith(r.prefix),
    );
    if (matchedPrefix) {
      const match = expiryStatuses.find(
        (s) => s.application_code === matchedPrefix.categoryCode,
      );
      if (match) return match;
    }

    return [...expiryStatuses].sort(
      (a, b) => SEVERITY[b.phase] - SEVERITY[a.phase],
    )[0];
  }, [expiryStatuses, location.pathname]);

  const dismissKey = expiryStatus
    ? `${expiryStatus.application_code || ""}:${expiryStatus.phase}`
    : null;

  if (!expiryStatus || !expiryStatus.show_banner || dismissedKey === dismissKey)
    return null;

  const isExpiringSoon = expiryStatus.phase === "expiring_soon";
  const isGracePeriod = expiryStatus.phase === "grace_period";
  const isExpired = expiryStatus.phase === "expired";
  const showRenewButton = isGracePeriod || isExpired;

  const bannerConfig = {
    expiring_soon: {
      bgcolor: alpha(theme.palette.warning.main, 0.1),
      borderColor: theme.palette.warning.main,
      icon: <WarningAmberIcon sx={{ fontSize: 20, color: "warning.main" }} />,
      textColor: "warning.dark",
      dismissable: true,
    },
    grace_period: {
      bgcolor: alpha(theme.palette.error.main, 0.08),
      borderColor: theme.palette.error.light,
      icon: <ErrorOutlineIcon sx={{ fontSize: 20, color: "error.main" }} />,
      textColor: "error.dark",
      dismissable: false,
    },
    expired: {
      bgcolor: alpha(theme.palette.error.main, 0.12),
      borderColor: theme.palette.error.main,
      icon: <ErrorOutlineIcon sx={{ fontSize: 20, color: "error.main" }} />,
      textColor: "error.dark",
      dismissable: false,
    },
  };

  const config = bannerConfig[expiryStatus.phase as keyof typeof bannerConfig];
  if (!config) return null;

  const message =
    expiryStatus.message ||
    (isExpiringSoon
      ? `Your plan expires in ${expiryStatus.days_until_expiry} day(s). Renew to avoid service interruption.`
      : isGracePeriod
        ? `Your plan has expired. Full access remains available for ${expiryStatus.grace_days_left} more day(s). Renew or pay the outstanding amount before the grace period ends.`
        : "Your plan has expired. Renew immediately to restore full access.");

  const handleRenew = () => {
    navigate("/platform-settings/billing/overview?action=renew");
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        px: 2.5,
        py: 1.2,
        mb: 0,
        bgcolor: config.bgcolor,
        borderBottom: `1px solid ${config.borderColor}`,
      }}
    >
      <Box display="flex" alignItems="center" gap={1.5} flex={1}>
        {config.icon}
        <Typography variant="body2" color={config.textColor} fontWeight={500}>
          {message}
        </Typography>
      </Box>

      <Box display="flex" alignItems="center" gap={1}>
        {showRenewButton && (
          <Button
            size="small"
            variant="contained"
            color="error"
            onClick={handleRenew}
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 1.5,
              fontSize: "0.8rem",
              whiteSpace: "nowrap",
            }}
          >
            Renew or Pay Due
          </Button>
        )}

        {config.dismissable && (
          <IconButton
            size="small"
            onClick={() => setDismissedKey(dismissKey)}
            sx={{ color: config.textColor }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}
      </Box>
    </Box>
  );
};

export default SubscriptionBanner;
